import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { BrokerContextType } from '../types/broker.types';
import { useAuth } from '../../auth/context/AuthContext';
import { BROKER_MODE_ENABLED } from '@common/config/environments';

const BrokerContext = createContext<BrokerContextType | undefined>(undefined);

export const BrokerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const brokerModeEnabledByEnv = BROKER_MODE_ENABLED;
  const [isBrokerMode, setIsBrokerModeState] = useState<boolean>(brokerModeEnabledByEnv);
  const [showBrokerWithUsModal, setShowBrokerWithUsModal] = useState<boolean>(false);
  const [showCreateAccountBrokerModal, setShowCreateAccountBrokerModal] = useState<boolean>(false);
  const { isBroker, isAuthenticated } = useAuth();

  const setIsBrokerMode = useCallback((value: boolean) => {
    setIsBrokerModeState(value);
  }, []);

  const toggleBrokerMode = useCallback(() => {
    setIsBrokerModeState(prev => !prev);
  }, []);

  const isBrokerEnabled = BROKER_MODE_ENABLED;

  useEffect(() => {
    if (isBroker && isAuthenticated) {
      setIsBrokerModeState(true);
    } else if (!isAuthenticated) {
      setIsBrokerModeState(brokerModeEnabledByEnv);
    } else if (isAuthenticated && !isBroker) {
      setIsBrokerModeState(false);
    }
  }, [isBroker, isAuthenticated, brokerModeEnabledByEnv]);

  const value: BrokerContextType = {
    isBrokerMode,
    showBrokerWithUsModal,
    showCreateAccountBrokerModal,
    setIsBrokerMode,
    setShowBrokerWithUsModal,
    setShowCreateAccountBrokerModal,
    toggleBrokerMode,
    isBrokerEnabled,
  };

  return (
    <BrokerContext.Provider value={value}>
      {children}
    </BrokerContext.Provider>
  );
};

export const useBroker = (): BrokerContextType => {
  const context = useContext(BrokerContext);
  if (context === undefined) {
    throw new Error('useBroker must be used within a BrokerProvider');
  }
  return context;
};
